/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/await-thenable */
import { FuelTypography, FuelRadio, FuelButton, FuelFilter, FuelDropdown } from '@frete.com/fuel-react'
import { TextField } from '~components'
import Form from '~components/Form'
import { CompanyUserData } from '~services/interfaces'
import { normalizeStringAccents } from '~utils/normalize'
import { Props, Values, SectorsData, SortType, ForwardRefProps, PermissionType } from './interfaces'
import * as Styled from './styled'
import { useRequest } from 'ahooks'
import { useState, useMemo, forwardRef, useImperativeHandle, useRef } from 'react'
import { Users } from '~services'
import FilterUserModal from '../FilterUserModal'
import {
  copyUsersRef,
  getListSort,
  sortEventType,
  filterEventTypePermissionId,
  filterEventTypeNumber,
  filterEventSectorId,
} from './utils'
import { Field, FormikProps } from 'formik'
import { useAmplitude, useGlobals, useTranslator } from '~hooks'
import { AmplitudeEvent } from '~constants/amplitude'
import { Text } from '~constants/text'

const Filters = forwardRef<ForwardRefProps, Props>(({ usersRef, handleSearch }, forwardedRef) => {
  const { translate } = useTranslator()
  const formRef = useRef<FormikProps<Values>>()
  const searchRef = useRef<HTMLDivElement>()
  const [useDepartaments, setDepartaments] = useState<SectorsData>([])
  const [openFilterModal, setOpenFilterModal] = useState(false)

  const dropdownRef = useRef<HTMLFuelDropdownElement>(null)

  const { user } = useGlobals()
  const { sendAmplitude } = useAmplitude()

  useImperativeHandle(forwardedRef, () => ({
    getFormValues() {
      const values = formRef.current.values

      const hasModalFilterAnyValue = values.permissionId || values.sectorId.length || values.typeNumber.length

      if (hasModalFilterAnyValue) {
        formRef.current.setFieldValue('permissionId', '')
        formRef.current.setFieldValue('sectorId', [])
        formRef.current.setFieldValue('typeNumber', [])

        setOpenFilterModal(true)

        handleSubmit({
          ...formRef.current.values,
          permissionId: null,
          sectorId: [],
          typeNumber: [],
        })
      }

      if (values.search) {
        formRef.current.setFieldValue('search', '')

        if (searchRef.current) {
          const input = searchRef.current.querySelector('input')
          if (input) input.value = ''

          if (!hasModalFilterAnyValue) {
            if (input) input.focus()
          }
        }

        handleSubmit({
          ...formRef.current.values,
          search: '',
        })
      }
    },
  }))

  useRequest(Users.getSectors, { onSuccess: (response) => setDepartaments(response.data) })

  const filter = useMemo(
    () => ({
      sectorId: (users: CompanyUserData[], param: number[]): CompanyUserData[] =>
        users?.filter((user: CompanyUserData) => param.includes(user.sectorId)),
      typeNumber: (users: CompanyUserData[], param: number[]): CompanyUserData[] =>
        users?.filter((user: CompanyUserData) => param.includes(user.phone.type)),
      permission: (users: CompanyUserData[], param: number): CompanyUserData[] =>
        users?.filter((user: CompanyUserData) => {
          return user.permissionId === param
        }),
      name: (users: CompanyUserData[], param: string): CompanyUserData[] =>
        users?.filter((user: CompanyUserData) =>
          normalizeStringAccents(user.name).includes(normalizeStringAccents(param.trim()))
        ),
      phone: (users: CompanyUserData[], param: string): CompanyUserData[] =>
        users?.filter((user: CompanyUserData) => {
          const getValueWithMask = user?.formattedPhone?.includes(param.trim())
          const getValueWithoutMask = user?.phone?.number?.includes(param.trim())

          return getValueWithMask || getValueWithoutMask
        }),
      email: (users: CompanyUserData[], param: string): CompanyUserData[] =>
        users?.filter((user: CompanyUserData) => user.email.includes(normalizeStringAccents(param.trim()))),
      departament: (users: CompanyUserData[], param: string): CompanyUserData[] => {
        const departament = useDepartaments.find((departament) =>
          normalizeStringAccents(departament.name).includes(normalizeStringAccents(param.trim()))
        )

        return departament ? users.filter((user) => user.sectorId === Number(departament.id)) : []
      },
    }),
    [useDepartaments]
  )

  function handleOpenFilterModal() {
    setOpenFilterModal(true)
  }

  function handleCloseFilterUser() {
    setOpenFilterModal(false)
  }

  function hasAnyParam(params: Values) {
    return params.search || params.permissionId || params.sectorId.length || params.typeNumber.length
  }

  function hasAnyModalParam(params: Values): boolean {
    return params.permissionId !== null || params.sectorId.length > 0 || params.typeNumber.length > 0
  }

  function validateSearch(params: Values): void {
    const currentUsersFilter = copyUsersRef(usersRef)
    let usersFilterResponse: CompanyUserData[] = []
    let usersFilterModal: CompanyUserData[] = currentUsersFilter

    if (params.search) {
      const searchByName = filter.name(
        usersFilterResponse.length ? usersFilterResponse : currentUsersFilter,
        params.search
      )

      usersFilterResponse = [...usersFilterResponse, ...searchByName]

      const searchByEmail = filter.email(
        usersFilterResponse.length ? usersFilterResponse : currentUsersFilter,
        params.search
      )

      usersFilterResponse = [...usersFilterResponse, ...searchByEmail]

      const searchByNumber = filter.phone(
        usersFilterResponse.length ? usersFilterResponse : currentUsersFilter,
        params.search
      )

      usersFilterResponse = [...usersFilterResponse, ...searchByNumber]

      const searchByDepartament = filter.departament(
        usersFilterResponse.length ? usersFilterResponse : currentUsersFilter,
        params.search
      )

      usersFilterResponse = [...usersFilterResponse, ...searchByDepartament]
    }

    if (params.permissionId) {
      usersFilterModal = [
        ...filter.permission(params.search ? usersFilterResponse : usersFilterModal, Number(params.permissionId)),
      ]
      usersFilterResponse = [...usersFilterModal]
    }

    if (params.typeNumber.length) {
      usersFilterModal = [
        ...filter.typeNumber(
          params.search ? usersFilterResponse : usersFilterModal,
          params.typeNumber.map((param) => Number(param))
        ),
      ]
      usersFilterResponse = [...usersFilterModal]
    }

    if (params.sectorId.length) {
      usersFilterModal = [
        ...filter.sectorId(
          params.search ? usersFilterResponse : usersFilterModal,
          params.sectorId.map((param) => Number(param))
        ),
      ]
      usersFilterResponse = [...usersFilterModal]
    }

    const uniqueIds = []

    const unique = usersFilterResponse.filter((element) => {
      const isDuplicate = uniqueIds.includes(element.id)

      if (!isDuplicate) {
        uniqueIds.push(element.id)

        return true
      }

      return false
    })

    const users = getListSort(unique, params.sort as SortType)

    handleSearch(users)
  }

  function onSearch(params: Values) {
    const users = copyUsersRef(usersRef)

    if (hasAnyParam(params)) {
      validateSearch(params)
    } else {
      const sortList = getListSort(users, params.sort as SortType)
      handleSearch(sortList)
    }
  }

  function handleSubmit(params: Values) {
    const users = copyUsersRef(usersRef)

    if (hasAnyParam(params)) {
      validateSearch(params)
    } else {
      const sortList = getListSort(users, params.sort as SortType)
      handleSearch(sortList)
    }
  }

  function onCancelSort(params: Values) {
    const users = copyUsersRef(usersRef)

    if (hasAnyParam(params)) {
      validateSearch(params)
    } else {
      handleSearch(users)
    }
  }

  function sendSortEvent(params: Values) {
    sendAmplitude(
      AmplitudeEvent.USER_MANAGEMENT_SORT_USERS,
      params.sort
        ? {
            sort: sortEventType[params.sort as SortType],
            user_email: user?.data?.email,
            user_id: user?.data?.id,
            company_id: user?.data?.company.id,
          }
        : null
    )
  }

  function sendFilterEvent(params: Values) {
    const filters = {}

    const { sectorId, permissionId, typeNumber } = params

    if (permissionId) {
      Object.assign(filters, {
        permission_type: filterEventTypePermissionId[params.permissionId as PermissionType],
      })
    }

    if (typeNumber.length) {
      const typeNumbers = []

      typeNumber.forEach((typeNumber) => {
        typeNumbers.push(filterEventTypeNumber[typeNumber])
      })

      Object.assign(filters, {
        contact_type: typeNumbers,
      })
    }

    if (sectorId.length) {
      const sectors = []

      sectorId.forEach((sector) => {
        sectors.push(filterEventSectorId[sector])
      })

      Object.assign(filters, {
        departament_type: sectors,
      })
    }

    sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_FILTER_USERS, filters)
  }

  async function resetFields() {
    await formRef.current.setFieldValue('permissionId', null)
    await formRef.current.setFieldValue('typeNumber', [])
    await formRef.current.setFieldValue('sectorId', [])
  }

  return (
    <>
      <Form<Values>
        initialValues={{ search: '', sort: null, permissionId: null, typeNumber: [], sectorId: [] }}
        onSubmit={(e) => {
          handleSubmit(e)
          sendFilterEvent(e)
        }}
        innerRef={formRef}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Styled.Subtitle>
            <Styled.Wrapper>
              <Styled.WrapperTextField ref={searchRef}>
                <Field
                  value={values?.search}
                  id="search"
                  type="search"
                  name="search"
                  placeholder={translate(Text.SEARCH_BY_NAME_EMAIL_DEPARTAMENT_PHONE)}
                  hasSuffix
                  data-testid="search"
                  onInput={(e: React.FormEvent<HTMLFuelTextFieldElement>) => {
                    const target = e.target as HTMLFuelTextFieldElement

                    onSearch({ ...values, search: target.value })
                    setFieldValue('search', target.value)
                  }}
                  component={TextField}
                />
              </Styled.WrapperTextField>

              <FuelFilter showDropdownIcon actived={values?.sort !== null} data-testid="search_by" variant="large">
                {translate(Text.ORDER_BY)}
                <FuelDropdown slot="dropdown" ref={dropdownRef}>
                  <FuelTypography tag="h1" size="heading-sm" weight="bold" slot="header">
                    {translate(Text.ORDER_BY)}
                  </FuelTypography>

                  <Styled.PopoverOptions slot="content" className="container">
                    <FuelRadio
                      data-testid="alphabetical_sort"
                      label={translate(Text.ALPHABETICAL_ORDER)}
                      value="0"
                      name="sort"
                      onInput={handleChange}
                      checked={values?.sort === '0'}
                    />
                    <FuelRadio
                      data-testid="register_sort_older"
                      label={translate(Text.OLDER_USER)}
                      value="1"
                      name="sort"
                      onInput={handleChange}
                      checked={values?.sort === '1'}
                    />

                    <FuelRadio
                      data-testid="register_sort_newer"
                      label={translate(Text.NEWER_USER)}
                      value="2"
                      name="sort"
                      onInput={handleChange}
                      checked={values?.sort === '2'}
                    />

                    <FuelRadio
                      data-testid="register_latest_access"
                      label={translate(Text.LATEST_ACCESS)}
                      value="3"
                      name="sort"
                      onInput={handleChange}
                      checked={values?.sort === '3'}
                    />
                  </Styled.PopoverOptions>

                  <FuelButton
                    slot="button"
                    variant="tertiary"
                    data-testid="cancel_sort"
                    onClick={() => {
                      onCancelSort(values)
                      setFieldValue('sort', null)
                      dropdownRef.current?.close()
                    }}
                  >
                    {translate(Text.CLEAR_FILTER, { multiple: '' })}
                  </FuelButton>
                  <FuelButton
                    slot="button"
                    type="button"
                    data-testid="submit_sort"
                    onClick={() => {
                      handleSubmit(values)
                      sendSortEvent(values)
                      dropdownRef.current?.close()
                    }}
                  >
                    {translate(Text.APPLY_FILTER, { multiple: '' })}
                  </FuelButton>
                </FuelDropdown>
              </FuelFilter>

              <Styled.WrapperChip $width="102px">
                <FuelFilter
                  variant="large"
                  showDropdownIcon={false}
                  data-testid="open_filter_modal"
                  actived={hasAnyModalParam(values)}
                  onClick={(e) => {
                    e.preventDefault()
                    handleOpenFilterModal()
                  }}
                >
                  Filtrar
                </FuelFilter>
              </Styled.WrapperChip>
            </Styled.Wrapper>
            <FilterUserModal
              open={openFilterModal}
              handleClose={handleCloseFilterUser}
              handleChange={handleChange}
              values={values}
              handleSubmit={() => {
                handleSubmit(values)
                sendFilterEvent(values)
              }}
              handleCancel={async () => {
                await resetFields()
                handleSubmit(formRef.current.values)
                setOpenFilterModal(false)
              }}
            />
          </Styled.Subtitle>
        )}
      </Form>
    </>
  )
})

export default Filters
